/* eslint-disable @typescript-eslint/naming-convention */
import * as signalR from '@microsoft/signalr';
import { useEffect, useRef, useState } from 'react';
import { appInsights } from '../../AppInsights';
import TokenService from '../../services/Auth/TokenService';

export default function useRealTimeNotification() {
  const [isConnected, setIsConnected] = useState<boolean>(false);

  const [hubConnection, _setHubConnection] = useState<signalR.HubConnection>();
  const hubConnectionRef = useRef(hubConnection);
  const setHubConnection = (instance: signalR.HubConnection) => {
    hubConnectionRef.current = instance;
    _setHubConnection(instance);
  };

  const startConnection = () => {
    const connection = new signalR.HubConnectionBuilder()
      .configureLogging(signalR.LogLevel.Error)
      .withUrl(`${process.env.REACT_APP_REALTIME_NOTIFICATION_NEGOTIATE_URL}`, {
        headers: {
          Authorization: sessionStorage.getItem(TokenService.accessTokenKey),
        } as signalR.MessageHeaders,
      } as signalR.IHttpConnectionOptions)
      .withAutomaticReconnect()
      .build();

    connection
      .start()
      .then(() => {
        console.info('Realtime messaging service connected');
        appInsights.trackTrace({ message: 'Realtime messaging service connected' });
        setIsConnected(true);
      })
      .catch((err) => {
        console.log(`Error while starting realtime connection: ${err}`);
        appInsights.trackTrace({ message: 'Error while starting realtime connection:' });
        appInsights.trackException({ exception: err });
      });
    return connection;
  };

  const addUserToGroup = (groupName: string): void => {
    if (!hubConnectionRef.current) {
      console.log('addUserToGroup: hubConnection instance not available');
      appInsights.trackTrace({ message: 'addUserToGroup: hubConnection instance not available' });
    }
    hubConnectionRef.current?.invoke('AddToGroup', groupName).catch((err) => appInsights.trackException({ exception: err }));
  };

  const removeUserFromGroup = (groupName: string): void => {
    if (!hubConnectionRef.current) {
      console.log('removeUserFromGroup: hubConnection instance not available');
      appInsights.trackTrace({ message: 'removeUserFromGroup: hubConnection instance not available' });
    }
    hubConnectionRef.current?.invoke('RemoveFromGroup', groupName).catch((err) => appInsights.trackException({ exception: err }));
  };

  const onMessageReceive = (messageName: string, callBackFunc: (...args: any[]) => any) => {
    if (!hubConnectionRef.current) {
      console.log('onMessageReceive: hubConnection instance not available');
      appInsights.trackTrace({ message: 'onMessageReceive: hubConnection instance not available' });
    }
    hubConnectionRef.current?.on(messageName, callBackFunc);
  };

  const stopConnection = (): void => {
    if (!hubConnectionRef.current) {
      console.log('stopConnection: hubConnection instance not available');
      appInsights.trackTrace({ message: 'stopConnection: hubConnection instance not available' });
    }
    hubConnectionRef.current?.stop();
  };

  useEffect(() => {
    const connection = startConnection();
    setHubConnection(connection);

    return () => {
      connection.stop();
    };
  }, []);

  return {
    addUserToGroup,
    removeUserFromGroup,
    onMessageReceive,
    stopConnection,
    isConnected,
  };
}
